import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Logo } from "../components/Logo";
import { CheckCircle2, AlertTriangle, Sparkles, ShieldCheck, ChevronRight, ChevronLeft } from "lucide-react";

/*
 * BILAN DIRIGEANTS — Page publique /bilan-dirigeants
 * Quiz d'auto-évaluation (14 questions · 5 sections) + formulaire de contact + résultat immédiat.
 * Décliné du /bilan-succession : même design system, mêmes patterns, contenu adapté aux chefs d'entreprise.
 */

const SECTIONS = {
  gouvernance: {
    label: "Gouvernance & dépendance",
    intro: "Que se passe-t-il si vous n'êtes plus là — même temporairement ?",
    items: [
      { key: "gouv_q1", label: "Si vous étiez indisponible demain (maladie, accident) pendant plusieurs semaines, l'entreprise pourrait-elle continuer à fonctionner normalement ?",
        options: [
          { v: "a", lbl: "Oui, tout est organisé et quelqu'un peut prendre le relais immédiatement" },
          { v: "b", lbl: "Partiellement — certaines décisions m'attendraient" },
          { v: "c", lbl: "Non, je suis le seul à détenir les informations clés" },
        ]},
      { key: "gouv_q2", label: "Avez-vous formalisé et communiqué qui prendrait les décisions à votre place en cas d'absence prolongée ?",
        options: [
          { v: "a", lbl: "Oui, un mandat ou une délégation de pouvoir est en place" },
          { v: "b", lbl: "J'y ai pensé, mais rien n'est signé" },
          { v: "c", lbl: "Non, ce sujet n'a jamais été abordé" },
        ]},
      { key: "gouv_q3", label: "Un tiers de confiance connaît-il l'ensemble de vos accès stratégiques (banques, contrats, mots de passe) ?",
        options: [
          { v: "a", lbl: "Oui, un document centralisé existe et est accessible" },
          { v: "b", lbl: "Certains éléments sont partagés, pas tout" },
          { v: "c", lbl: "Non, tout est dans ma tête" },
        ]},
    ],
  },
  structure: {
    label: "Structure & valorisation",
    intro: "Votre entreprise est-elle prête à être transmise dans de bonnes conditions ?",
    items: [
      { key: "struct_q4", label: "Avez-vous fait estimer la valeur de votre entreprise au cours des trois dernières années ?",
        options: [
          { v: "a", lbl: "Oui, une valorisation récente existe" },
          { v: "b", lbl: "Une estimation approximative, non formalisée" },
          { v: "c", lbl: "Jamais" },
        ]},
      { key: "struct_q5", label: "Vos statuts prévoient-ils clairement ce qui se passe en cas de décès ou d'incapacité d'un dirigeant ou associé ?",
        options: [
          { v: "a", lbl: "Oui, une clause spécifique existe" },
          { v: "b", lbl: "Les statuts en parlent vaguement" },
          { v: "c", lbl: "Non, ou je ne sais pas" },
        ]},
      { key: "struct_q6", label: "Si vous avez des associés, existe-t-il une clause de rachat des parts en cas de décès, financée (assurance croisée) ?",
        options: [
          { v: "a", lbl: "Oui, formalisée et financée" },
          { v: "b", lbl: "Prévue, mais non financée" },
          { v: "c", lbl: "Aucune clause, ou pas d'associés concernés" },
        ]},
    ],
  },
  transmission: {
    label: "Transmission & fiscalité",
    intro: "La transmission de votre entreprise a-t-elle été préparée fiscalement et humainement ?",
    items: [
      { key: "trans_q7", label: "Avez-vous une idée claire de qui reprendrait l'entreprise (enfant, associé, cadre, cession externe) ?",
        options: [
          { v: "a", lbl: "Oui, un successeur est identifié et informé" },
          { v: "b", lbl: "Une piste, non confirmée" },
          { v: "c", lbl: "Aucune idée pour l'instant" },
        ]},
      { key: "trans_q8", label: "Un plan de transmission (calendrier, mode : donation, cession, holding) a-t-il été engagé avec un professionnel ?",
        options: [
          { v: "a", lbl: "Oui, en cours avec un conseil" },
          { v: "b", lbl: "Y avoir pensé, rien de formel" },
          { v: "c", lbl: "Non" },
        ]},
      { key: "trans_q9", label: "Connaissez-vous les dispositifs fiscaux de transmission d'entreprise (Pacte Dutreil, donation-partage, holding) et leur impact sur votre situation ?",
        options: [
          { v: "a", lbl: "Oui, j'ai déjà été conseillé sur le sujet" },
          { v: "b", lbl: "J'en ai entendu parler, sans plus" },
          { v: "c", lbl: "Non" },
        ]},
    ],
  },
  protection: {
    label: "Protection de la famille",
    intro: "Vos proches sont-ils protégés des risques liés à l'entreprise ?",
    items: [
      { key: "prot_q10", label: "En cas de décès, votre conjoint(e) ou vos héritiers seraient-ils protégés financièrement pendant la transition de l'entreprise ?",
        options: [
          { v: "a", lbl: "Oui, une assurance homme-clé ou un dispositif est en place" },
          { v: "b", lbl: "Partiellement" },
          { v: "c", lbl: "Non, ou je ne sais pas" },
        ]},
      { key: "prot_q11", label: "Avez-vous distingué votre patrimoine professionnel de votre patrimoine personnel pour protéger votre famille des risques de l'entreprise ?",
        options: [
          { v: "a", lbl: "Oui, clairement structuré" },
          { v: "b", lbl: "Partiellement" },
          { v: "c", lbl: "Non" },
        ]},
    ],
  },
  anticipation: {
    label: "Anticipation & documents",
    intro: "Vos volontés et documents essentiels sont-ils écrits, connus et accessibles ?",
    items: [
      { key: "antic_q12", label: "Avez-vous rédigé un testament ou des directives précisant vos volontés concernant l'entreprise ?",
        options: [
          { v: "a", lbl: "Oui" },
          { v: "b", lbl: "En cours de rédaction" },
          { v: "c", lbl: "Non" },
        ]},
      { key: "antic_q13", label: "Vos proches et associés savent-ils où trouver les documents essentiels (statuts, contrats, assurances, contacts des conseils) en cas de coup dur ?",
        options: [
          { v: "a", lbl: "Oui, tout est centralisé et accessible" },
          { v: "b", lbl: "Partiellement" },
          { v: "c", lbl: "Non" },
        ]},
      { key: "antic_q14", label: "À quand remonte la dernière fois que vous avez pris le temps, avec un professionnel, de réfléchir sérieusement à l'avenir de votre entreprise sans vous ?",
        options: [
          { v: "a", lbl: "Moins d'un an" },
          { v: "b", lbl: "Plus de deux ans" },
          { v: "c", lbl: "Jamais vraiment" },
        ]},
    ],
  },
};

const SECTION_KEYS = ["gouvernance", "structure", "transmission", "protection", "anticipation"];

const POINTS = { a: 3, b: 1, c: 0 };

const DIAGNOSTIC_META = {
  risque:      { label: "Zone à risque", color: "#b53a3a", icon: AlertTriangle,
    message: "Votre entreprise repose presque entièrement sur vous. En l'état, votre absence mettrait rapidement en péril son activité, vos salariés et votre famille. C'est une situation fréquente — et réversible dès aujourd'hui." },
  vigilance:   { label: "Vigilance", color: "#caa86a", icon: Sparkles,
    message: "Quelques réflexes existent, mais l'essentiel n'est pas formalisé. En cas de coup dur, vos proches et vos équipes manqueraient d'informations ou de cadre pour agir vite et sereinement." },
  bien_engage: { label: "Bien engagé", color: "#1d3a72", icon: ShieldCheck,
    message: "Vous avez déjà posé des bases solides. Il reste des zones d'ombre à sécuriser pour que la transmission de votre entreprise se passe exactement comme vous le souhaitez." },
  exemplaire:  { label: "Exemplaire", color: "#2f7d5b", icon: CheckCircle2,
    message: "Vous faites partie des rares dirigeants à avoir sérieusement anticipé ce sujet. Un point d'expert peut encore affiner certains détails — fiscalité, gouvernance — pour verrouiller ce que vous avez construit." },
};

const SECTEURS = [
  "Industrie / production",
  "Commerce / distribution",
  "Services aux entreprises (B2B)",
  "Services aux particuliers (B2C)",
  "Bâtiment / travaux publics",
  "Santé / médico-social",
  "Restauration / hôtellerie",
  "Technologie / logiciel",
  "Finance / conseil",
  "Agriculture / viticulture",
  "Immobilier",
  "Autre",
];

const REGISTER_URL = "https://www.auprea.com/inscription";

function computeLocalScore(reponses) {
  const sections = {};
  let total = 0;
  for (const sk of SECTION_KEYS) {
    let s = 0, m = 0;
    for (const q of SECTIONS[sk].items) {
      s += (POINTS[reponses[q.key]] ?? 0);
      m += 3;
    }
    sections[sk] = { score: s, max: m };
    total += s;
  }
  let diagnostic = "vigilance";
  if (total <= 13) diagnostic = "risque";
  else if (total >= 35) diagnostic = "exemplaire";
  else if (total >= 25) diagnostic = "bien_engage";
  // Weak = 2 sections avec pct le plus faible
  const weak = Object.entries(sections)
    .sort((a, b) => (a[1].score / a[1].max) - (b[1].score / b[1].max))
    .slice(0, 2)
    .map(([k]) => k);
  return { sections, total, diagnostic, weak };
}

export default function BilanDirigeants() {
  // Étapes : 0-4 = 5 sections, 5 = coordonnées, 6 = résultat
  const [step, setStep] = useState(0);
  const [reponses, setReponses] = useState({});
  const [coords, setCoords] = useState({
    prenom: "", nom: "", entreprise: "", email: "", telephone: "", secteur_activite: "",
  });
  const [consent, setConsent] = useState(false);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (typeof document !== "undefined") document.title = "Bilan pour les pros · AUPRÉA";
  }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const isQuestionStep = step >= 0 && step <= 4;
  const currentSection = isQuestionStep ? SECTIONS[SECTION_KEYS[step]] : null;

  const canGoNext = useMemo(() => {
    if (isQuestionStep) {
      return currentSection.items.every((q) => ["a", "b", "c"].includes(reponses[q.key]));
    }
    if (step === 5) {
      const emailOk = /\S+@\S+\.\S+/.test(coords.email);
      return coords.prenom.trim() && coords.nom.trim() && coords.entreprise.trim()
        && emailOk && coords.telephone.trim() && consent;
    }
    return true;
  }, [step, isQuestionStep, currentSection, reponses, coords, consent]);

  const totalSteps = 6; // 5 sections + 1 contact
  const progressPct = Math.round(((step + 1) / totalSteps) * 100);

  const goNext = async () => {
    if (!canGoNext) return;
    if (step < 5) {
      setStep(step + 1);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    const local = computeLocalScore(reponses);
    try {
      const { data } = await api.post("/public/bilan-dirigeants/submit", {
        prenom: coords.prenom.trim(),
        nom: coords.nom.trim(),
        entreprise: coords.entreprise.trim(),
        email: coords.email.trim(),
        telephone: coords.telephone.trim(),
        secteur_activite: coords.secteur_activite,
        consentement_rgpd: consent,
        reponses,
      });
      setResult({
        id: data.id,
        score_total: data.score_total,
        score_max: data.score_max,
        score_sections: data.score_sections,
        weak_sections: data.weak_sections,
        diagnostic: data.diagnostic,
      });
      setStep(6);
    } catch (err) {
      setResult({
        id: null,
        score_total: local.total,
        score_max: 42,
        score_sections: local.sections,
        weak_sections: local.weak,
        diagnostic: local.diagnostic,
      });
      setStep(6);
      setSubmitError("Votre bilan est affiché, mais un souci réseau a empêché son enregistrement. Merci de nous recontacter à contact@auprea.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: "var(--auprea-cream)", minHeight: "100vh" }} data-testid="bilan-dirigeants-page">
      {/* Header minimaliste : logo seul */}
      <nav className="flex items-center justify-center px-6 py-6 border-b" style={{ borderColor: "rgba(21,41,79,0.1)" }}>
        <Link to="/" data-testid="bilan-dirigeants-logo-home">
          <Logo size={28} />
        </Link>
      </nav>

      {/* Barre de progression */}
      {step <= 5 && (
        <div className="max-w-3xl mx-auto px-6 pt-8" data-testid="bilan-dirigeants-progress">
          <div className="flex items-center justify-between mb-2" style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(35,38,46,0.55)" }}>
            <span>Étape {step + 1} / {totalSteps}</span>
            <span>{progressPct} %</span>
          </div>
          <div style={{ height: 4, background: "rgba(21,41,79,0.08)", borderRadius: 2, overflow: "hidden" }}>
            <div style={{ width: `${progressPct}%`, height: "100%", background: "var(--auprea-gold)", transition: "width 0.4s ease" }} />
          </div>
        </div>
      )}

      <main className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        {/* Intro seulement à l'étape 0 */}
        {step === 0 && (
          <div className="mb-10 text-center" data-testid="bilan-dirigeants-intro">
            <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>— Bilan pour les pros</div>
            <h1 style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: "clamp(28px, 4.6vw, 44px)", fontWeight: 500, lineHeight: 1.2, margin: 0 }}>
              Si vous vous arrêtiez demain, votre entreprise tiendrait-elle sans vous&nbsp;?
            </h1>
            <p style={{ fontSize: 17, color: "rgba(35,38,46,0.75)", marginTop: 18, lineHeight: 1.6 }}>
              La plupart des dirigeants consacrent toute leur énergie à faire grandir leur entreprise — et repoussent
              sans cesse la question de ce qu'elle deviendrait en leur absence. Ce bilan en 14 questions révèle vos
              angles morts en matière de gouvernance, de transmission et de protection de vos proches.
            </p>
            <div style={{ marginTop: 22, fontSize: 13, color: "rgba(35,38,46,0.55)", fontStyle: "italic" }}>
              « Un dirigeant averti en vaut deux. »
            </div>
          </div>
        )}

        {/* Étape questions */}
        {isQuestionStep && currentSection && (
          <div data-testid={`bilan-dirigeants-step-${SECTION_KEYS[step]}`}>
            <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>
              Section {step + 1} / 5
            </div>
            <h2 style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: 28, fontWeight: 500, margin: 0 }}>
              {currentSection.label}
            </h2>
            <p style={{ fontSize: 15, color: "rgba(35,38,46,0.7)", margin: "10px 0 32px 0" }}>{currentSection.intro}</p>

            <div className="space-y-8">
              {currentSection.items.map((q, idx) => (
                <div key={q.key} data-testid={`q-${q.key}`}>
                  <div style={{ fontSize: 15, color: "var(--auprea-ink)", marginBottom: 12, lineHeight: 1.5 }}>
                    <strong style={{ color: "var(--auprea-navy)", marginRight: 6 }}>{idx + 1}.</strong> {q.label}
                  </div>
                  <div className="space-y-2">
                    {q.options.map((opt) => {
                      const sel = reponses[q.key] === opt.v;
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => setReponses((r) => ({ ...r, [q.key]: opt.v }))}
                          data-testid={`answer-${q.key}-${opt.v}`}
                          style={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            padding: "12px 16px",
                            border: `1px solid ${sel ? "var(--auprea-gold)" : "rgba(21,41,79,0.15)"}`,
                            background: sel ? "rgba(202,168,106,0.12)" : "#fff",
                            color: sel ? "var(--auprea-navy)" : "var(--auprea-ink)",
                            fontSize: 14,
                            fontWeight: sel ? 600 : 500,
                            cursor: "pointer",
                            borderRadius: 4,
                            transition: "all 0.15s ease",
                            lineHeight: 1.5,
                          }}
                        >
                          {opt.lbl}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Étape coordonnées (formulaire de contact) */}
        {step === 5 && (
          <div data-testid="bilan-dirigeants-step-coords">
            <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>
              Dernière étape
            </div>
            <h2 style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: 28, fontWeight: 500, margin: 0 }}>
              Recevez votre bilan personnalisé
            </h2>
            <p style={{ fontSize: 14, color: "rgba(35,38,46,0.7)", margin: "8px 0 28px 0" }}>
              Vos coordonnées nous permettent de vous envoyer votre bilan par email et, si vous le souhaitez, de vous rappeler pour un point de 15 minutes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Prénom *" testid="input-prenom" value={coords.prenom} onChange={(v) => setCoords((c) => ({ ...c, prenom: v }))} />
              <Field label="Nom *" testid="input-nom" value={coords.nom} onChange={(v) => setCoords((c) => ({ ...c, nom: v }))} />
              <div className="md:col-span-2">
                <Field label="Entreprise *" testid="input-entreprise" value={coords.entreprise} onChange={(v) => setCoords((c) => ({ ...c, entreprise: v }))} placeholder="Raison sociale" />
              </div>
              <Field label="Email professionnel *" testid="input-email" type="email" value={coords.email} onChange={(v) => setCoords((c) => ({ ...c, email: v }))} />
              <Field label="Téléphone *" testid="input-tel" value={coords.telephone} onChange={(v) => setCoords((c) => ({ ...c, telephone: v }))} placeholder="06 12 34 56 78" />
              <div className="md:col-span-2">
                <label style={{ fontSize: 11, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, display: "block", marginBottom: 6 }}>
                  Secteur d'activité (facultatif)
                </label>
                <select
                  className="auprea-input"
                  value={coords.secteur_activite}
                  onChange={(e) => setCoords((c) => ({ ...c, secteur_activite: e.target.value }))}
                  data-testid="input-secteur"
                  style={{ width: "100%", background: "#fff" }}
                >
                  <option value="">— Choisir un secteur —</option>
                  {SECTEURS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <label
              className="flex items-start gap-3 mt-6 cursor-pointer"
              data-testid="consent-label"
              style={{ fontSize: 13, color: "rgba(35,38,46,0.75)", lineHeight: 1.55 }}
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="input-consent"
                style={{ marginTop: 3, width: 16, height: 16, accentColor: "var(--auprea-navy)", flexShrink: 0 }}
              />
              <span>
                J'accepte que mes données soient traitées par AUPRÉA pour recevoir mon bilan et éventuellement être
                recontacté(e). Conformément au RGPD, je peux à tout moment demander leur suppression à{" "}
                <a href="mailto:contact@auprea.com" style={{ color: "var(--auprea-blue)" }}>contact@auprea.com</a>.
              </span>
            </label>
          </div>
        )}

        {/* Étape résultat */}
        {step === 6 && result && (
          <ResultBlock result={result} submitError={submitError} />
        )}

        {/* Navigation */}
        {step <= 5 && (
          <div className="flex items-center justify-between mt-12 gap-3" data-testid="bilan-dirigeants-nav">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} className="auprea-btn-outline" style={{ padding: "12px 22px" }} data-testid="btn-prev">
                <ChevronLeft size={16} style={{ marginRight: 6 }} /> Précédent
              </button>
            ) : <span />}
            <button
              onClick={goNext}
              disabled={!canGoNext || submitting}
              data-testid="btn-next"
              style={{
                padding: "14px 28px",
                background: canGoNext ? "var(--auprea-navy)" : "rgba(21,41,79,0.35)",
                color: "#fff",
                border: "none",
                fontWeight: 600,
                fontSize: 15,
                borderRadius: 4,
                cursor: canGoNext && !submitting ? "pointer" : "not-allowed",
                letterSpacing: "0.03em",
                transition: "background 0.15s ease",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {submitting ? "Traitement…" : (step === 5 ? "Voir mon diagnostic" : "Suivant")}
              {!submitting && <ChevronRight size={16} />}
            </button>
          </div>
        )}
      </main>

      <footer className="text-center py-8 border-t" style={{ borderColor: "rgba(21,41,79,0.08)", color: "rgba(35,38,46,0.55)", fontSize: 12 }}>
        AUPRÉA · <em>Anticiper est un acte de leadership.</em>
      </footer>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", placeholder = "", inputMode }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: "rgba(35,38,46,0.7)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, display: "block", marginBottom: 6 }}>{label}</label>
      <input
        className="auprea-input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        data-testid={testid}
        style={{ width: "100%", background: "#fff" }}
      />
    </div>
  );
}

function ResultBlock({ result, submitError }) {
  const meta = DIAGNOSTIC_META[result.diagnostic] || DIAGNOSTIC_META.vigilance;
  const Icon = meta.icon;
  const weak = result.weak_sections || [];
  return (
    <div data-testid="bilan-dirigeants-step-result">
      {submitError && (
        <div style={{ marginBottom: 20, padding: "12px 16px", background: "rgba(181,58,58,0.08)", border: "1px solid rgba(181,58,58,0.25)", color: "#b53a3a", fontSize: 13, borderRadius: 4 }} data-testid="submit-warning">
          {submitError}
        </div>
      )}

      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 12 }}>Votre bilan personnalisé</div>
        <div style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', color: "var(--auprea-navy)", fontSize: 64, fontWeight: 500, lineHeight: 1 }} data-testid="score-total">
          {result.score_total} <span style={{ fontSize: 30, color: "rgba(35,38,46,0.4)" }}>/ {result.score_max || 42}</span>
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            marginTop: 16,
            padding: "8px 20px",
            background: meta.color,
            color: "#fff",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            borderRadius: 999,
          }}
          data-testid={`badge-diagnostic-${result.diagnostic}`}
        >
          <Icon size={16} /> {meta.label}
        </div>
        <p style={{ maxWidth: 620, margin: "22px auto 0", fontSize: 15, color: "rgba(35,38,46,0.75)", lineHeight: 1.6 }} data-testid="diagnostic-message">
          {meta.message}
        </p>
      </div>

      {/* Priorités à traiter (2 sections faibles) */}
      {weak.length > 0 && (
        <div
          style={{
            background: "rgba(202,168,106,0.1)",
            border: "1px solid rgba(202,168,106,0.35)",
            padding: "20px 22px",
            borderRadius: 6,
            marginBottom: 24,
          }}
          data-testid="weak-sections"
        >
          <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>
            Vos 2 priorités à traiter
          </div>
          <ul style={{ paddingLeft: 20, margin: 0, fontSize: 15, lineHeight: 1.7, color: "var(--auprea-navy)" }}>
            {weak.map((k) => (
              <li key={k} data-testid={`weak-${k}`}>
                <strong>{SECTIONS[k]?.label || k}</strong>
                {" — "}
                <span style={{ color: "rgba(35,38,46,0.7)", fontSize: 14 }}>
                  {result.score_sections?.[k]?.score ?? 0} / {result.score_sections?.[k]?.max ?? 0}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Détail sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8" data-testid="section-scores">
        {SECTION_KEYS.map((sk) => {
          const s = result.score_sections?.[sk];
          if (!s) return null;
          return <SectionBar key={sk} label={SECTIONS[sk].label} value={s.score} max={s.max} />;
        })}
      </div>

      {/* CTA final */}
      <div style={{ background: "var(--auprea-navy)", color: "#f3efe6", padding: "26px 28px", borderRadius: 6, textAlign: "center" }} data-testid="bilan-dirigeants-cta">
        <div style={{ color: "var(--auprea-gold)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 10 }}>Aller plus loin</div>
        <div style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', fontSize: 22, fontWeight: 500, marginBottom: 10 }}>
          Structurez la transmission de votre entreprise avec AUPRÉA
        </div>
        <div style={{ fontSize: 14, color: "rgba(243,239,230,0.85)", marginBottom: 18, lineHeight: 1.6, maxWidth: 560, margin: "0 auto 18px" }}>
          Centralisez vos statuts, contrats, assurances et volontés dans un carnet sécurisé — et bénéficiez d'un
          accompagnement par des experts (notaires, avocats, conseils en gestion de patrimoine).
        </div>
        <a
          href={REGISTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cta-inscription-final"
          style={{
            display: "inline-block",
            padding: "14px 30px",
            background: "var(--auprea-gold)",
            color: "var(--auprea-navy)",
            textDecoration: "none",
            fontWeight: 700,
            fontSize: 14,
            borderRadius: 4,
            letterSpacing: "0.03em",
          }}
        >
          Créer mon compte AUPRÉA
        </a>
      </div>
    </div>
  );
}

function SectionBar({ label, value, max }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ background: "#fff", border: "1px solid rgba(21,41,79,0.08)", padding: "16px 18px", borderRadius: 6 }}>
      <div style={{ fontSize: 12, color: "rgba(35,38,46,0.65)", marginBottom: 8, lineHeight: 1.4 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 8 }}>
        <span style={{ fontFamily: '"Playfair Display","Spectral", Georgia, serif', fontSize: 22, color: "var(--auprea-navy)", fontWeight: 500 }}>{value}</span>
        <span style={{ fontSize: 13, color: "rgba(35,38,46,0.5)" }}>/ {max}</span>
      </div>
      <div style={{ height: 4, background: "rgba(21,41,79,0.08)", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: "var(--auprea-gold)" }} />
      </div>
    </div>
  );
}
